import { ModelHelper } from '../../helpers/model_helper';
import { template } from '../../template';

function activeTimingsMatch(model, timingId) {
  return (model.get('activeTimings') ?? []).some((timingRef) => timingRef._id === timingId);
}

function openDetailsModal(model, collection) {
  const DetailsViewClass = ModelHelper.getDetailViewClassForModelType(model.Type, model);
  const detailsView = new DetailsViewClass({ model, collection });
  app.modalManager.showModalWithView(detailsView);
}

// One entry per entity type that can reference a day part. Menu structures point at a single day
// part via the scalar `menuTimingId`; the other four reference it through the `activeTimings` array.
const listConfigs = [
  {
    key: 'menu-structure',
    category: 'Menu',
    unit: 'menu',
    getList() {
      return app.menuStructureList;
    },
    matches(model, timingId) {
      return model.get('menuTimingId') === timingId;
    },
    onClick(model) {
      app.router.navigate(`menus/${model.id}`, { trigger: true });
    },
  },
  {
    key: 'menu-section',
    category: 'Menu Section',
    unit: 'menu section',
    getList() {
      return app.menuSectionList;
    },
    matches: activeTimingsMatch,
    onClick: openDetailsModal,
  },
  {
    key: 'menu-item',
    category: 'Menu Item',
    unit: 'menu item',
    getList() {
      return app.menuItemList;
    },
    matches: activeTimingsMatch,
    onClick: openDetailsModal,
  },
  {
    key: 'mod-group',
    category: 'Mod Group',
    unit: 'mod group',
    getList() {
      return app.modGroupList;
    },
    matches: activeTimingsMatch,
    onClick: openDetailsModal,
  },
  {
    key: 'mod',
    category: 'Modifier',
    unit: 'modifier',
    getList() {
      return app.modList;
    },
    matches: activeTimingsMatch,
    onClick: openDetailsModal,
  },
];

app.MenuTimingDetailsView = app.BaseDetailsView.extend({
  template: template($('#menu-item-array-details-template').html()),

  initialize() {
    app.BaseDetailsView.prototype.initialize.apply(this, arguments);

    this._dropdownByKey = {};

    if (!this.model.isNew()) {
      // The Day Parts tab is reachable without visiting the Library/Menu tabs, which is where these
      // lists are normally fetched. Fetch any that are still empty and re-render as they arrive.
      listConfigs.forEach((config) => {
        const list = config.getList();
        if (!list) {
          return;
        }
        if (!list.hasBeenFetched()) {
          list.fetch({ reset: true });
        }
        this.listenTo(list, 'reset sync add remove change', this._renderUsageLists);
      });
    }
  },

  _matchingModels(config) {
    const list = config.getList();
    if (!list) {
      return [];
    }
    return list.filter((model) => config.matches(model, this.model.id));
  },

  _renderUsageLists() {
    if (!this._$usageLists) {
      return;
    }

    Object.values(this._dropdownByKey).forEach((dropdown) => dropdown.destroy());
    this._dropdownByKey = {};
    this._$usageLists.empty();

    listConfigs.forEach((config) => {
      const models = this._matchingModels(config);
      if (!models.length) {
        return;
      }

      const list = config.getList();
      const $panel = $(
        this.template({
          canAddItems: false,
          canCreateItems: false,
          itemCategory: config.category,
        }),
      ).addClass(`${config.key}-list`);
      this._$usageLists.append($panel);

      const dropdown = new app.Dropdown();
      this._dropdownByKey[config.key] = dropdown;
      dropdown.setupRows(
        $panel,
        models.map((model) => model.id),
        list,
        {
          onClick(model) {
            config.onClick(model, list);
          },
          canBeRemoved() {
            return false;
          },
        },
      );

      $panel
        .find('.card-footer')
        .show()
        .html(`${models.length} ${config.unit}${models.length === 1 ? '' : 's'}`);
    });
  },

  getDeleteConfirmation() {
    const usageParts = listConfigs
      .map((config) => {
        const count = this._matchingModels(config).length;
        return count ? `${count} ${config.unit}${count === 1 ? '' : 's'}` : null;
      })
      .filter((part) => part);

    if (!usageParts.length) {
      return app.BaseDetailsView.prototype.getDeleteConfirmation.apply(this);
    }

    return (
      `This day part is used by ${usageParts.join(', ')}. Deleting it will remove the day part ` +
      `from all of them. Are you sure you want to delete this day part?`
    );
  },

  render() {
    app.BaseDetailsView.prototype.render.apply(this);

    if (!this.model.isNew()) {
      // Base render replaces the contents of this.$el, so recreate the container each render.
      this._$usageLists = $('<div class="usage-lists"></div>');
      this.$el.append(this._$usageLists);
      this._renderUsageLists();
    }

    return this;
  },
});
